import { Link } from "react-router-dom";
import { AlertTriangle, Flag, X, BookOpen } from "lucide-react";

/** Reusable 3-flag streak popup. Used today for the tongue-tie tie_signs signal; the
 *  same component can drive any future check-in-triggered flag signal by passing a
 *  different `signalLabel` and `learnHref`. */
export default function StreakFlagModal({ streak, signalLabel, learnHref, onClose }) {
  if (!streak || streak < 1) return null;
  const escalated = streak >= 3;
  const ordinal = streak === 1 ? "1st" : streak === 2 ? "2nd" : "3rd";
  const title = escalated ? "This keeps coming up" : "We noticed this";
  const body = escalated
    ? `${signalLabel} has come up for three check-ins in a row. It may be time for a closer evaluation — we'd recommend reaching out for a consultation.`
    : `This is your ${ordinal} flag for ${signalLabel}. If this keeps showing up, we'll recommend reaching out for a closer look.`;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-navy/50 backdrop-blur-sm px-4" data-testid="streak-flag-modal" role="dialog" aria-modal="true">
      <div className={`w-full max-w-md bg-warm-white rounded-3xl shadow-2xl border ${escalated ? "border-mauve" : "border-edge"} overflow-hidden`}>
        <div className={`px-6 py-5 flex items-start gap-3 ${escalated ? "bg-mauve-pale" : "bg-cream"}`}>
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${escalated ? "bg-mauve text-warm-white" : "bg-gold/25 text-navy"}`}>
            {escalated ? <AlertTriangle size={18} /> : <Flag size={18} />}
          </div>
          <div className="flex-1">
            <div className="font-display text-lg font-bold text-navy" data-testid="streak-flag-title">{title}</div>
            <div className="text-xs uppercase tracking-widest font-semibold mt-0.5" style={{ color: escalated ? "#8B5E7A" : "#7a7364" }} data-testid="streak-flag-ordinal">
              {escalated ? "3 flags in a row" : `Flag ${streak} of 3`}
            </div>
          </div>
          <button type="button" onClick={onClose} data-testid="streak-flag-close" aria-label="Close" className="text-ink-mid hover:text-navy">
            <X size={18} />
          </button>
        </div>
        <div className="px-6 py-5">
          <p className="text-sm text-ink-dark leading-relaxed" data-testid="streak-flag-body">{body}</p>

          {/* 3-dot streak indicator, mirrors the roadblock card */}
          <div className="mt-4 flex items-center justify-center gap-2" data-testid="streak-flag-dots">
            {[1, 2, 3].map((i) => (
              <div key={i} className={`w-8 h-8 rounded-full flex items-center justify-center border-2 transition ${i <= streak ? (escalated ? "bg-mauve border-mauve text-warm-white" : "bg-gold border-gold text-ink-dark") : "border-edge text-ink-soft"}`} data-testid={`streak-flag-dot-${i}`}>
                <Flag size={14} strokeWidth={2.5} />
              </div>
            ))}
          </div>

          <div className="mt-6 flex items-center justify-between gap-3 flex-wrap">
            {learnHref ? (
              <Link to={learnHref} onClick={onClose} data-testid="streak-flag-learn" className="inline-flex items-center gap-1.5 text-xs font-semibold text-navy underline hover:text-mauve">
                <BookOpen size={12} /> Learn more (optional)
              </Link>
            ) : <span />}
            <button type="button" onClick={onClose} data-testid="streak-flag-dismiss" className={`px-5 py-2.5 rounded-full text-sm font-semibold ${escalated ? "bg-mauve text-warm-white hover:bg-mauve/90" : "bg-gold text-ink-dark hover:bg-gold-light"}`}>
              {escalated ? "Got it" : "Okay, thanks"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
